import { Box, Chip, Stack, Typography } from "@mui/material";
import { BiggerThanSmallAndSmallerThanMediumText } from "../../common/styles/Text.styles";
import {
  iconGap,
  perelynLightGreen,
  perelynLightRed,
  perelynMediumGreen,
  smallGap,
  tinyGap,
} from "../../styles/consts";

interface Keyword {
  keyword: string;
  count: number;
  source: "required" | "nice_to_have" | "tag";
}

interface KeywordAnalysisData {
  keywords: Keyword[];
  summary: {
    total: number;
    present: number;
    missing: number;
  };
}

interface KeywordAnalysisProps {
  keywordAnalysis: KeywordAnalysisData;
  baselineKeywordAnalysis?: KeywordAnalysisData;
}

const SOURCE_LABELS: Record<string, string> = {
  required: "Required Skills",
  nice_to_have: "Nice-to-Have Skills",
  tag: "Tags",
};

const SOURCE_ORDER = ["required", "nice_to_have", "tag"];

type ChipState = "baseline-present" | "improvement" | "missing";

const CHIP_STYLES: Record<ChipState, { bg: string; color: string }> = {
  "baseline-present": { bg: perelynLightGreen, color: "#1B5E20" },
  improvement: { bg: perelynMediumGreen, color: "#1B5E20" },
  missing: { bg: perelynLightRed, color: "#B71C1C" },
};

export default function KeywordAnalysis({
  keywordAnalysis,
  baselineKeywordAnalysis,
}: KeywordAnalysisProps) {
  if (
    !keywordAnalysis ||
    !keywordAnalysis.keywords ||
    keywordAnalysis.keywords.length === 0
  ) {
    return null;
  }

  const { keywords, summary } = keywordAnalysis;

  // When no baseline is provided (e.g. legacy matches), treat all currently
  // present keywords as baseline-present — there's no prior state to compare
  // against, so we cannot identify any "improvement" chips.
  const hasBaseline = baselineKeywordAnalysis !== undefined;

  const baselinePresent = new Set<string>();
  for (const kw of baselineKeywordAnalysis?.keywords ?? []) {
    if (kw.count > 0) {
      baselinePresent.add(kw.keyword.toLowerCase());
    }
  }

  const classify = (kw: Keyword): ChipState => {
    if (kw.count <= 0) return "missing";
    if (!hasBaseline) return "baseline-present";
    return baselinePresent.has(kw.keyword.toLowerCase())
      ? "baseline-present"
      : "improvement";
  };

  const total = summary.total;
  let baselineCovered = 0;
  let improvementCovered = 0;
  for (const kw of keywords) {
    const state = classify(kw);
    if (state === "baseline-present") baselineCovered += 1;
    else if (state === "improvement") improvementCovered += 1;
  }
  const baselinePct = total > 0 ? (baselineCovered / total) * 100 : 0;
  const improvementPct = total > 0 ? (improvementCovered / total) * 100 : 0;

  const grouped: Record<string, Keyword[]> = {};
  for (const kw of keywords) {
    if (!grouped[kw.source]) grouped[kw.source] = [];
    grouped[kw.source].push(kw);
  }

  return (
    <Stack sx={{ padding: smallGap, paddingTop: 0, gap: smallGap }}>
      <BiggerThanSmallAndSmallerThanMediumText>
        Keyword Coverage
      </BiggerThanSmallAndSmallerThanMediumText>

      <Stack sx={{ gap: tinyGap }}>
        <Typography sx={{ fontSize: "0.875rem" }}>
          {summary.present}/{summary.total} keywords covered
        </Typography>
        <Box
          sx={{
            position: "relative",
            height: 6,
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: perelynLightRed,
          }}
        >
          <Box
            data-bar-segment="baseline"
            style={{ width: `${baselinePct}%`, left: 0 }}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              backgroundColor: perelynLightGreen,
            }}
          />
          <Box
            data-bar-segment="improvement"
            style={{ width: `${improvementPct}%`, left: `${baselinePct}%` }}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              backgroundColor: perelynMediumGreen,
            }}
          />
        </Box>
      </Stack>

      {SOURCE_ORDER.map((source) => {
        const group = grouped[source];
        if (!group || group.length === 0) return null;

        return (
          <Stack key={source} sx={{ gap: tinyGap }}>
            <Typography
              sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#666" }}
            >
              {SOURCE_LABELS[source] || source}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: iconGap }}>
              {group.map((kw) => {
                const state = classify(kw);
                const style = CHIP_STYLES[state];
                return (
                  <Chip
                    key={`${source}-${kw.keyword}`}
                    data-state={state}
                    label={
                      kw.count > 0 ? `${kw.keyword} (${kw.count})` : kw.keyword
                    }
                    size="small"
                    sx={{
                      backgroundColor: style.bg,
                      color: style.color,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      border: "none",
                      "& .MuiChip-label": { padding: "0 8px" },
                    }}
                  />
                );
              })}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}
